/**
 * Forgot-password page (USR-007) — step 1 of the reset flow.
 *
 * The client enters their e-mail; the API sends a reset link to
 * `/reset?token=…` if the address has an account. The page always shows
 * the same confirmation, whatever the API knows, so it cannot be used to
 * find out which addresses are registered.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ApiError, requestPasswordReset } from '@/lib/api-client';

export function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const schema = z.object({
    email: z.string().min(1, t('login.errors.required')).email(t('login.errors.invalidEmail')),
  });
  type Fields = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Fields>({ resolver: zodResolver(schema), defaultValues: { email: '' } });

  const onSubmit = async (data: Fields): Promise<void> => {
    setSubmitError(null);
    try {
      await requestPasswordReset(data.email.trim());
      setSent(true);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 429) {
        setSubmitError(t('passwordReset.errors.rateLimited'));
      } else {
        setSubmitError(t('login.errors.networkError'));
      }
    }
  };

  return (
    <section className="max-w-md mx-auto px-s5 py-s8">
      <header className="mb-s6">
        <h1 className="font-display text-display-lg text-ink">{t('passwordReset.request.title')}</h1>
        <p className="mt-s3 text-gray-2">{t('passwordReset.request.subtitle')}</p>
      </header>

      {sent ? (
        <div className="space-y-s4" data-testid="reset-request-sent">
          <p role="status" className="text-ink">
            {t('passwordReset.request.sent')}
          </p>
          <Link to="/login" className="text-sm text-flame-ember hover:underline">
            {t('passwordReset.backToLogin')}
          </Link>
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
          noValidate
          className="space-y-s4"
        >
          <div>
            <label htmlFor="email" className="form-label">
              {t('login.fields.email')}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t('login.placeholders.email')}
              className={`input-field ${errors.email ? 'input-error' : ''}`}
              {...register('email')}
            />
            {errors.email?.message && <p className="form-error">{errors.email.message}</p>}
          </div>

          {submitError && (
            <p role="alert" className="form-error" data-testid="submit-error">
              {submitError}
            </p>
          )}

          <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
            {isSubmitting ? t('passwordReset.request.submitting') : t('passwordReset.request.submit')}
          </button>

          <div className="text-center">
            <Link to="/login" className="text-sm text-flame-ember hover:underline">
              {t('passwordReset.backToLogin')}
            </Link>
          </div>
        </form>
      )}
    </section>
  );
}
